import React, { useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { useToast } from '@common/toast';
import Icon from '@common/assets';
import CancelBtn from '@components/profile/profileOverlay/overlays/common/cancelBtn';
import RemoveBtn from '@components/profile/profileOverlay/overlays/common/removeBtn';
import { CareerStateType } from '@components/profile/profileOverlay/overlays/career/index';
import { useDeleteProfileCareer } from '@repository/profiles/useProfileCareer';
import ProfileCareersVO, { CareersVO } from '@domain/vo/profile/ProfileCareersVO';

import Colors from '@domain/constant/colors';
import classnames from 'classnames/bind';
import styles from './career.module.scss';
const cx = classnames.bind(styles);

interface CareerListProps {
  careers?: CareersVO[];
  setDeleteList: (deleteList: (prev: CareersVO[]) => CareersVO[]) => void;
}

const CareerList = ({ careers, setDeleteList }: CareerListProps) => {
  const listEls = careers?.map((career, index) => {
    const { companyName, jobGroupName, jobTitleName, verified } = career;
    const handleClick = () => {
      setDeleteList((prev) => [...prev, career]);
    };

    const getDesc = () => {
      if (!jobGroupName && !jobTitleName) return '직군 · 직무를 입력해 주세요.';
      if (!jobTitleName) return '직무를 입력해 주세요.';
      return `${jobGroupName} > ${jobTitleName}`;
    };

    return (
      <li key={index} className={cx('listItem')}>
        <div className={cx('btnList', 'delete')}>
          <div className={cx('contentsArea')}>
            <div className={cx('listTextArea')}>
              <span className={cx('listText')}>{companyName}</span>
              {verified && <Icon name={'verifiedLightFilled'} width={24} height={24} fill={Colors.C_GREEN_150} />}
            </div>
            <span className={cx('listDesc')}>{getDesc()}</span>
          </div>

          <RemoveBtn onClick={handleClick} />
        </div>
      </li>
    );
  });

  return <ul className={cx('list')}>{listEls}</ul>;
};

const EmptyList = () => {
  return <div className={cx('emptyList')}>입력하신 경력 정보가 없어요.</div>;
};

interface CareerDeleteListProps {
  refetch: () => Promise<QueryObserverResult>;
  profileCareer?: ProfileCareersVO | undefined;
  setCareerState: (careerState: CareerStateType) => void;
  openExitModal: () => void;
}

const CareerDeleteList = ({ refetch, profileCareer, setCareerState, openExitModal }: CareerDeleteListProps) => {
  const Toast = useToast();
  const [deleteList, setDeleteList] = useState<CareersVO[]>([]);
  const isUpdated = deleteList.length !== 0;

  const { mutateAsync: deleteProfileCareer } = useDeleteProfileCareer();

  const handlePrev = () => {
    if (isUpdated) {
      openExitModal();
      return;
    }

    setCareerState('LIST');
  };

  const handleSubmit = async () => {
    const deletePromises = deleteList.map(async ({ sn }) => sn !== null && (await deleteProfileCareer(sn)));
    await Promise.all(deletePromises);
    await refetch().then(() => {
      Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
      setCareerState('LIST');
    });
  };

  const getList = () =>
    profileCareer?.careers.filter((career) => !deleteList.find((deleteCareer) => deleteCareer === career));

  const getListEl = () => {
    if (deleteList.length === profileCareer?.careers.length) return <EmptyList />;
    return <CareerList careers={getList()} setDeleteList={setDeleteList} />;
  };

  return (
    <>
      <div className={cx('scrollArea')}>
        <div className={cx('scrollInner')}>
          <div className={cx('scrollWrap')}>
            <div className={cx('listArea')}>
              {getListEl()}
              <div className={cx('cancelArea')}>
                <CancelBtn onClick={() => setCareerState('LIST')} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={cx('btnArea')}>
        <a className={cx('btn', 'cancel')} role="button" onClick={handlePrev}>
          이전
        </a>
        <a className={cx('btn', 'confirm')} role="button" onClick={handleSubmit}>
          저장
        </a>
      </div>
    </>
  );
};

export default CareerDeleteList;
